// In some cases, we want to enable new features only for specific countries or environments, or a combination of both.
// This file contains the configuration for those features.
// The configuration is stored in the `featureFlags` object, which contains a mapping of feature names to their respective configurations.

export type Env = "test" | "qa" | "prod";

export enum Feature {
  SERECA = "sereca",
  CRITEO = "criteo",
}

export interface FeatureConfig {
  countries?: string[];
  countriesByEnv?: Partial<Record<Env, string[] | undefined>>;
  envs: Env[];
}

// central registry of every feature’s rollout config
// countries is an array of country codes where the feature is enabled.
// If countries is not set, the feature is enabled for all countries. If set to an empty array, the feature is disabled for all countries.
// countriesByEnv overrides the country list for a specific environment.
// envs is an array of environments where the feature is enabled
export const featureFlags: Record<Feature, FeatureConfig> = {
  [Feature.SERECA]: {
    // Sereca gridboxes for Slider
    countries: ["SK", "BE", "CZ", "NL", "PL", "FR", "DE", "ES"],
    envs: ["test", "qa", "prod"],
  },
  [Feature.CRITEO]: {
    countries: ["DE", "FR"],
    envs: ["qa", "prod"],
  },
};

/**
 * Gets the list of countries for a specific environment.
 * If countriesByEnv is defined for the environment, it returns that. Otherwise, it returns the default countries list for that feature.
 * @param cfg The feature configuration object
 * @param env The environment to get the countries for
 * @returns The list of countries for the specified environment, or undefined if the feature is enabled for all countries
 */
const getCountriesForEnv = (cfg: FeatureConfig, env: Env): string[] | undefined =>
  cfg.countriesByEnv?.[env] ?? cfg.countries;

/**
 * This function determines the current environment based on the hostname.
 * It checks if the hostname starts with "test.", "qa.", or defaults to "prod.".
 * @returns The current environment based on the hostname.
 */
export function currentEnv(): Env {
  const host = window.location?.hostname?.toLowerCase() ?? "";
  if (host.startsWith("test.")) return "test";
  if (host.startsWith("qa.")) return "qa";
  return "prod";
}

/**
 * This function checks if a feature should be used for a specific country and environment.
 * The specific configuration for each feature is stored in the `featureFlags` object.
 * @param featureKey The name of the feature to check
 * @param country The country code to check
 * @returns A boolean indicating whether the feature should be used for that country and environment
 */
export function shouldUseFeature(featureKey: Feature, country: string): boolean {
  const host = window.location?.hostname?.toLowerCase() ?? "";
  if (host === "localhost" || window.location.href.includes("story")) return true;

  const cfg = featureFlags[featureKey];
  if (!cfg) return false;

  const env = currentEnv();
  const inEnv = cfg.envs.includes(env);

  if (!country) return inEnv;

  const countries = getCountriesForEnv(cfg, env);

  // If countries is not defined, the feature is enabled for all countries
  // If countries is defined, check if the current country is in the list
  // If countries is an empty array, the feature is disabled for all countries
  const inCountry = countries === undefined ? true : countries.length > 0 && countries.includes(country);

  return inEnv && inCountry;
}
